import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '~/lib/api';
import { useSession, type KybStatus } from '~/lib/session-context';
import type { Program } from '~/lib/programs';

/**
 * Tenant Overview. Four real counts across the top, the tenant's own
 * programs on the left, a placeholder for authorizations on the right
 * (that endpoint ships in a later PR). The platform-admin "Businesses"
 * table was removed — this view is a tenant looking at their own
 * business, not Seven looking at every tenant.
 */

interface Customer {
  id: string;
}
interface ApiKey {
  id: string;
  status: 'active' | 'revoked';
}

type Loadable<T> =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ok'; data: T };

export function HomePage(): JSX.Element {
  const { me, kybStatus } = useSession();
  const [programs, setPrograms] = useState<Loadable<Program[]>>({ kind: 'loading' });
  const [customers, setCustomers] = useState<Loadable<Customer[]>>({ kind: 'loading' });
  const [keys, setKeys] = useState<Loadable<ApiKey[]>>({ kind: 'loading' });

  useEffect(() => {
    let active = true;
    async function load<T>(
      path: string,
      pick: (res: { data: T[] } | T[]) => T[],
      set: (v: Loadable<T[]>) => void,
    ): Promise<void> {
      try {
        const res = await api<{ data: T[] } | T[]>(path);
        if (!active) return;
        set({ kind: 'ok', data: pick(res) });
      } catch (err) {
        if (!active) return;
        set({
          kind: 'error',
          message:
            err instanceof ApiError ? err.message : `Could not load ${path}`,
        });
      }
    }
    void load<Program>('/dashboard/programs', (r) => ('data' in r ? r.data : r), setPrograms);
    void load<Customer>('/dashboard/customers', (r) => ('data' in r ? r.data : r), setCustomers);
    void load<ApiKey>('/dashboard/api-keys', (r) => (Array.isArray(r) ? r : r.data), setKeys);
    return () => {
      active = false;
    };
  }, []);

  const activeKeys =
    keys.kind === 'ok' ? keys.data.filter((k) => k.status === 'active').length : null;

  return (
    <div className="mx-auto w-full max-w-[1280px] px-10 pt-6 pb-12">
      <div className="mb-5">
        <h1 className="text-[22px] font-semibold -tracking-[0.015em]">
          {me ? me.tenant.name : 'Overview'}
        </h1>
        <p className="mt-1 text-[14.5px] text-ink-secondary">
          What's live right now across your Seven account.
        </p>
      </div>

      {/* Metric strip */}
      <div className="grid grid-cols-1 overflow-hidden rounded-lg border border-rule-hairline bg-surface-slip sm:grid-cols-2 lg:grid-cols-4">
        <Metric
          label="Programs"
          value={programs.kind === 'ok' ? String(programs.data.length) : '—'}
          loading={programs.kind === 'loading'}
          empty={programs.kind === 'ok' && programs.data.length === 0}
        />
        <Metric
          label="Cardholders"
          value={customers.kind === 'ok' ? String(customers.data.length) : '—'}
          loading={customers.kind === 'loading'}
          empty={customers.kind === 'ok' && customers.data.length === 0}
        />
        <Metric
          label="Active API keys"
          value={activeKeys !== null ? String(activeKeys) : '—'}
          loading={keys.kind === 'loading'}
          empty={activeKeys === 0}
        />
        <KybMetric status={kybStatus} tenantStatus={me?.tenant.status ?? 'pending_kyb'} />
      </div>

      {/* Two-up: programs list + authorizations placeholder */}
      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[420px_1fr]">
        <ProgramsCard state={programs} />
        <AuthorizationsPlaceholder />
      </div>
    </div>
  );
}

// ---------- metrics ----------

function Metric({
  value,
  label,
  loading,
  empty,
}: {
  value: string;
  label: string;
  loading?: boolean;
  empty?: boolean;
}): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-2 border-b border-r border-rule-hairline p-5 last:border-r-0 sm:border-b-0">
      <div className="flex items-baseline gap-2">
        <span
          className={`font-sans text-[32px] font-semibold leading-none -tracking-[0.02em] ${
            loading ? 'text-ink-watermark' : empty ? 'text-ink-watermark' : ''
          }`}
        >
          {value}
        </span>
      </div>
      <div className="text-[14px] text-ink-secondary">{label}</div>
    </div>
  );
}

function KybMetric({
  status,
  tenantStatus,
}: {
  status: KybStatus;
  tenantStatus: 'pending_kyb' | 'active' | 'suspended';
}): JSX.Element {
  const copy =
    tenantStatus === 'suspended'
      ? { label: 'Business verification', text: 'Suspended', tone: 'text-stop' }
      : tenantStatus === 'active'
        ? { label: 'Business verification', text: 'Approved', tone: 'text-tally' }
        : status === 'submitted'
          ? { label: 'Business verification', text: 'Submitted', tone: 'text-footnote' }
          : status === 'in_review'
            ? { label: 'Business verification', text: 'In review', tone: 'text-footnote' }
            : status === 'rejected'
              ? { label: 'Business verification', text: 'Rejected', tone: 'text-stop' }
              : { label: 'Business verification', text: 'Not started', tone: 'text-notice' };

  const linkable = tenantStatus !== 'active';
  return (
    <div className="flex min-w-0 flex-col gap-2 border-b border-r border-rule-hairline p-5 last:border-r-0 sm:border-b-0">
      <div className="flex items-baseline gap-2">
        <span
          className={`font-sans text-[22px] font-semibold leading-tight -tracking-[0.01em] ${copy.tone}`}
        >
          {copy.text}
        </span>
      </div>
      <div className="text-[14px] text-ink-secondary">
        {copy.label}
        {linkable && (
          <>
            {' · '}
            <Link to="/kyb" className="font-medium text-ink-primary hover:underline">
              Open
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

// ---------- programs card ----------

function ProgramsCard({ state }: { state: Loadable<Program[]> }): JSX.Element {
  return (
    <div className="rounded-lg border border-rule-hairline bg-surface-slip">
      <div className="flex items-center justify-between border-b border-rule-hairline px-5 py-3">
        <span className="text-[14px] font-semibold text-ink-primary">Programs</span>
        <Link
          to="/programs"
          className="font-mono text-[13px] text-ink-watermark hover:text-ink-primary"
        >
          {state.kind === 'ok' ? `${state.data.length} total` : ''}
        </Link>
      </div>

      {state.kind === 'loading' && (
        <div className="px-5 py-6 text-[14px] text-ink-secondary">Loading…</div>
      )}
      {state.kind === 'error' && (
        <div className="px-5 py-4 text-[14px] text-stop">{state.message}</div>
      )}
      {state.kind === 'ok' && state.data.length === 0 && (
        <div className="flex flex-col items-start gap-3 px-5 py-6">
          <p className="text-[14px] leading-[18px] text-ink-secondary">
            No programs yet. Create one to start issuing cards under a chosen
            network and funding model.
          </p>
          <Link
            to="/programs"
            className="inline-flex h-8 items-center rounded-md bg-ink-primary px-3 text-[13.5px] font-semibold text-ink-on-dark hover:bg-ink-primary/90"
          >
            Create your first program
          </Link>
        </div>
      )}
      {state.kind === 'ok' &&
        state.data.map((p) => (
          <div
            key={p.id}
            className="flex items-center justify-between border-b border-rule-hairline px-5 py-3 last:border-b-0"
          >
            <div className="flex items-center gap-2.5">
              <span className="h-1.5 w-1.5 rounded-full bg-tally" />
              <span className="font-mono text-[14px] text-ink-primary">
                {p.displayName}
              </span>
            </div>
            <span className="font-mono text-[13px] text-ink-watermark">
              {p.fundingMode} · {p.product.currency}
            </span>
          </div>
        ))}
    </div>
  );
}

// ---------- authorizations placeholder ----------

function AuthorizationsPlaceholder(): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col rounded-lg border border-rule-hairline bg-surface-slip">
      <div className="flex items-center justify-between border-b border-rule-hairline px-5 py-3">
        <span className="text-[14px] font-semibold text-ink-primary">
          Authorizations
        </span>
        <span className="font-mono text-[13px] text-ink-watermark">
          Last 30 days
        </span>
      </div>
      <div className="grid min-h-[200px] place-items-center px-5 pb-6 pt-6 text-center">
        <div className="max-w-[36ch]">
          <div className="text-[14px] font-medium text-ink-primary">
            Nothing to chart yet
          </div>
          <p className="mt-1 text-[13.5px] leading-[18px] text-ink-watermark">
            Once your first card runs its first authorization, the 30-day
            trend lands here.
          </p>
        </div>
      </div>
    </div>
  );
}
